import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { singleSpaPropsSubject } from '../../single-spa/single-spa-props';
import { AuthService } from './auth.service';
import { DominioOrigen } from '../enums/dominio-origen';
import {
  DataLayerGlobals,
  ErrorData,
  GtmInteractionOptions,
  PageViewData,
  UIInteractionData,
} from '../interfaces/tag-manager.types';

type WindowWithGtm = Window & {
  dataLayer?: Record<string, unknown>[];
  google_tag_manager?: Record<string, unknown>;
};

@Injectable({
  providedIn: 'root'
})
export class TagManagerService {
  private static readonly EVENT_TIMEOUT_MS = 2000;
  private static readonly GTM_CONTAINER_ID = 'GTM-ND73TR5';

  private readonly window: WindowWithGtm;
  private readonly authService = inject(AuthService);
  private gtmInjectionStarted = false;

  constructor() {
    this.window = window;
    this.ensureGtmLoaded();
  }

  /**
   * Inicializa dataLayer e inyecta GTM si el shell (MF) no lo cargó.
   * Mismo snippet que index.html para reutilizar el contenedor compartido.
   */
  private ensureGtmLoaded(): void {
    if (typeof this.window?.document === 'undefined') {
      return;
    }

    this.ensureDataLayer();

    if (this.isGtmContainerLoaded() || this.isGtmScriptPresent()) {
      return;
    }

    if (this.gtmInjectionStarted) {
      return;
    }
    this.gtmInjectionStarted = true;

    this.window.dataLayer!.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const doc = this.window.document;
    const firstScript = doc.getElementsByTagName('script')[0];
    const gtmScript = doc.createElement('script');
    gtmScript.async = true;
    gtmScript.src =
      `https://www.googletagmanager.com/gtm.js?id=${TagManagerService.GTM_CONTAINER_ID}`;
    firstScript?.parentNode?.insertBefore(gtmScript, firstScript);

    
  }

  private ensureDataLayer(): void {
    if (!this.window.dataLayer) {
      this.window.dataLayer = [];
    }
  }

  private isGtmContainerLoaded(): boolean {
    return !!this.window.google_tag_manager?.[TagManagerService.GTM_CONTAINER_ID];
  }

  private isGtmScriptPresent(): boolean {
    return !!this.window.document.querySelector(
      `script[src*="googletagmanager.com/gtm.js?id=${TagManagerService.GTM_CONTAINER_ID}"]`
    );
  }

  private pushEvent(hit: Record<string, unknown>): void {
    this.ensureGtmLoaded();
    const dataLayer = this.window.dataLayer;
    if (!dataLayer) {
      return;
    }
    dataLayer.push(hit);
  }

  private resolveDominioFromProps(): DominioOrigen {
    const props = singleSpaPropsSubject.value;
    const rawDominio = String(props.Dominio ?? '').trim().toLowerCase();
    const rawWhitelabel = String(props.whitelabel ?? '').trim().toLowerCase();
    const rawCompanyName = String(props.companyName ?? '').trim().toLowerCase();

    if (rawDominio === '2' || rawWhitelabel.includes('agilice') || rawDominio.includes('agilice') || rawCompanyName.includes('agilice')) {
      return DominioOrigen.Agilice;
    }
    if (rawDominio === '3' || rawWhitelabel.includes('redmat') || rawDominio.includes('redmat') || rawCompanyName.includes('redmat')) {
      return DominioOrigen.Redmat;
    }
    return DominioOrigen.Iconstruye;
  }

  private getProductNameFromDominio(): string {
    const dominio = this.resolveDominioFromProps();
    if (dominio === DominioOrigen.Agilice) {
      return 'agilice';
    }
    if (dominio === DominioOrigen.Redmat) {
      return 'redmat';
    }
    return 'iconstruye';
  }

  /**
   * Obtiene las variables globales para el Data Layer.
   * Identidad desde AuthService.getIdentidadUsuario(); fallback a props del shell single-spa.
   */
  private getGlobalVariables(empresaNombreOverride?: string | null): DataLayerGlobals {
    const fromSession = this.authService.getIdentidadUsuario();
    const props = singleSpaPropsSubject.value;

    return {
      idUsuario: fromSession?.IdUsuario ?? props.IdUsuario?.trim() ?? null,
      idEmpresa: fromSession?.IdEmpresa ?? props.IdEmpresa ?? null,
      id_organizacion: fromSession?.IdOrganizacion ?? props.IdOrg ?? null,
      empresa_nombre: empresaNombreOverride
        ?? this.authService.getEmpresaNombre()
        ?? props.companyName?.trim()
        ?? null,
      app_name: 'MENU-ASTERIC',
      environment: environment.production ? 'production' : 'development',
      product_name: "TRANSVERSAL",
      domain: this.getProductNameFromDominio().toString()
    };
  }

  /**
   * Registra un evento de vista de página (page_view).
   * Se dispara cada vez que una página o vista se carga o cambia.
   */
  logPageView(pageData: PageViewData, empresaNombreOverride?: string | null): void {
    this.pushEvent({
      event: 'page_view',
      ...this.getGlobalVariables(empresaNombreOverride),
      ...pageData,
    });
  }

  /**
   * Registra un evento de uso de característica principal (feature_used)
   * Se dispara cuando el usuario activa o utiliza una funcionalidad clave
   */
  logFeatureUsed(featureName: string, featureCategory: string, additionalData?: Record<string, unknown>): void {
    this.pushEvent({
      event: 'feature_used',
      ...this.getGlobalVariables(),
      feature_name: featureName,
      feature_category: featureCategory,
      ...(additionalData || {})
    });
  }

  /**
   * Registra un evento de interacción de UI (ui_interaction).
   * Si hay navegación tras el clic, pasar `options.onComplete` para usar el patrón
   * GTM `eventCallback` + `eventTimeout` y no cancelar el beacon.
   */
  logUIInteraction(interactionData: UIInteractionData, options?: GtmInteractionOptions): void {
    const onComplete = options?.onComplete;

    const payload: Record<string, unknown> = {
      event: 'ui_interaction',
      ...this.getGlobalVariables(options?.empresa_nombre),
      ...interactionData
    };

    if (onComplete) {
      let completed = false;
      const runOnce = (): void => {
        if (completed) {
          return;
        }
        completed = true;
        onComplete();
      };

      payload['eventCallback'] = runOnce;
      payload['eventTimeout'] = TagManagerService.EVENT_TIMEOUT_MS;

      // Respaldo propio: si GTM no carga (bloqueado/caído), eventCallback nunca se invoca.
      setTimeout(runOnce, TagManagerService.EVENT_TIMEOUT_MS);
    }

    this.ensureGtmLoaded();
    this.pushEvent(payload);
  }

  /**
   * Registra un evento de error (dl_error).
   * Se dispara cuando ocurre un error en el microfrontend.
   */
  logError(errorData: ErrorData, empresaNombreOverride?: string | null): void {
    this.pushEvent({
      event: 'dl_error',
      ...this.getGlobalVariables(empresaNombreOverride),
      ...errorData,
    });
  }
}
